// The hub's read, shared by the three tabs: the state the device holds at
// once, then the server's when it arrives; back to the launch path when no
// one is signed in, and "Tentar outra vez" after a failure.

import { useEffect, useState } from "react";

import type { DataLayer, HubState } from "../data";
import { paths, useRouter } from "../lib/router";
import type { AssessmentId, AssessmentRun } from "../schema/types";

/** The signed-in account and the hub's read; the app passes its Onboarding or Perfil seam, which have both. */
export interface HubSource {
  accountId(): Promise<string | null>;
  data(): Pick<DataLayer, "loadHub">;
}

export function codeOf(error: unknown): string {
  if (typeof error === "object" && error !== null && "code" in error) return String(error.code);
  if (error instanceof Error) return error.name;
  return typeof error;
}

/** `screen` names the log line: « [hub] could not read the assessments ». */
export function useHub(source: HubSource, screen: string) {
  const { navigate } = useRouter();
  const [hub, setHub] = useState<HubState | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const accountId = await source.accountId();
      if (!alive) return;
      if (!accountId) {
        navigate(paths.entry, { replace: true });
        return;
      }
      const local = await source.data().loadHub(accountId, (fresh) => {
        if (alive) setHub(fresh);
      });
      if (alive) setHub((shown) => shown ?? local);
    };
    load().catch((error: unknown) => {
      console.error(`[${screen}] could not read the assessments`, { code: codeOf(error) });
      if (alive) setFailed(true);
    });
    return () => {
      alive = false;
    };
    // Reads once per visit, and again on "Tentar outra vez".
  }, [attempt]);

  const retry = () => {
    setFailed(false);
    setAttempt((count) => count + 1);
  };
  /** An assessment's run as the hub holds it, the argument the lib's hub rules take. */
  const runOf = (assessmentId: AssessmentId): AssessmentRun | null =>
    hub?.assessments.find((entry) => entry.assessmentId === assessmentId)?.run ?? null;
  return { hub, failed, retry, runOf };
}
